import React from 'react';
import { AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Sessionstorage flag so the auto-reload never loops in case the chunk
// is genuinely missing on the server (e.g. mis-deploy). After one reload
// we fall back to the visible error UI instead of refreshing forever.
const RELOAD_FLAG = 'chunk_error_reload_attempted';

// Heuristic: a stale-deploy chunk error from Webpack/CRA looks like:
//   "Loading chunk 9064 failed"
//   "ChunkLoadError"
//   "Failed to fetch dynamically imported module"
//   "error loading dynamically imported module"
//   "Importing a module script failed"
const isChunkLoadError = (err) => {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const msg = (err.message || '').toLowerCase();
  return (
    msg.includes('loading chunk') ||
    msg.includes('chunkloaderror') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('importing a module script failed')
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, reloading: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    this.setState({ errorInfo });

    // Silently ship the error to the backend so we can debug user-reported
    // crashes that never reach the developer console. Best-effort fetch,
    // never throws back into the boundary.
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      const payload = {
        message: String(error?.message || error || 'unknown'),
        stack: String(error?.stack || ''),
        component_stack: String(errorInfo?.componentStack || ''),
        url: typeof window !== 'undefined' ? window.location.href : '',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '',
      };
      // Use fetch with keepalive so the report survives a navigation.
      fetch(`${backend}/api/_client_errors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch (_) { /* noop */ }

    // Stale-deploy chunk error → cached main.js asking for a chunk hash
    // the new deploy no longer ships. Auto-reload once, bypassing the SW
    // cache, instead of forcing the user to learn about Ctrl+Shift+R.
    if (isChunkLoadError(error)) {
      const alreadyTried = sessionStorage.getItem(RELOAD_FLAG);
      if (!alreadyTried) {
        sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
        this.setState({ reloading: true });
        // Refresh the Service Worker (NOT unregister — that would destroy
        // the browser's push subscription) and drop caches, then reload.
        const finish = () => {
          window.location.reload();
        };
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistrations()
            .then((regs) => Promise.all(regs.map((r) => r.update())))
            .catch(() => {})
            .finally(() => {
              if (window.caches && window.caches.keys) {
                window.caches.keys()
                  .then((keys) => Promise.all(keys.map((k) => window.caches.delete(k))))
                  .catch(() => {})
                  .finally(finish);
              } else {
                finish();
              }
            });
        } else {
          finish();
        }
      }
    }
  }

  handleHardReload = () => {
    // Drop SW caches + reload (sin desregistrar el SW: eso mataría la
    // suscripción push). Useful when the user lands on an outdated bundle.
    const finish = () => window.location.reload();
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations()
        .then((regs) => Promise.all(regs.map((r) => r.update())))
        .catch(() => {})
        .finally(() => {
          if (window.caches && window.caches.keys) {
            window.caches.keys()
              .then((keys) => Promise.all(keys.map((k) => window.caches.delete(k))))
              .catch(() => {})
              .finally(finish);
          } else {
            finish();
          }
        });
    } else {
      finish();
    }
  };

  handleGoHome = () => {
    // Context-aware redirect: a crash on the PUBLIC booking page should
    // send the patient back to `/`, NOT to `/admin/agenda` (admin-only,
    // would redirect through login and look broken to the patient).
    const path = typeof window !== 'undefined' ? window.location.pathname : '/';
    const target = path.startsWith('/admin') ? '/admin/agenda' : '/';
    this.handleReset();
    window.location.href = target;
  };

  handleReset = () => {
    sessionStorage.removeItem(RELOAD_FLAG);
    this.setState({ hasError: false, error: null, errorInfo: null, reloading: false });
  };

  render() {
    if (this.state.reloading) {
      // Friendly loader while we drop SW caches and reload — much better
      // than showing the technical stack trace for a transient deploy
      // issue the user shouldn't have to understand.
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
          <Loader2 className="w-10 h-10 text-emerald-500 animate-spin mb-4" />
          <h2 className="text-base font-semibold text-slate-800 mb-1">Actualizando…</h2>
          <p className="text-sm text-slate-500 max-w-md">
            Detectada nueva versión de la aplicación. Recargando para aplicar los cambios.
          </p>
        </div>
      );
    }

    if (this.state.hasError) {
      const errorMessage = this.state.error?.message || 'Error desconocido';
      const errorStack = this.state.errorInfo?.componentStack || '';

      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-brand-deep mb-4" />
          <h2 className="text-lg font-semibold text-slate-800 mb-2">
            Algo salió mal
          </h2>
          <p className="text-sm text-slate-500 mb-4 max-w-md">
            Ha ocurrido un error inesperado. Puedes intentar recargar esta sección o volver al inicio.
          </p>
          <details className="mb-6 max-w-lg text-left">
            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
              Detalles del error (para soporte técnico)
            </summary>
            <pre className="mt-2 p-3 bg-gray-100 rounded text-xs text-red-600 overflow-auto max-h-40 whitespace-pre-wrap">
              {errorMessage}
              {errorStack && `\n\nComponente:\n${errorStack.slice(0, 500)}`}
            </pre>
          </details>
          <div className="flex gap-3 flex-wrap justify-center">
            <Button onClick={this.handleReset} variant="outline" className="gap-2">
              <RefreshCw size={16} />
              Reintentar
            </Button>
            <Button onClick={this.handleHardReload} variant="outline" className="gap-2" data-testid="error-hard-reload-btn">
              <RefreshCw size={16} />
              Recargar (sin caché)
            </Button>
            <Button onClick={this.handleGoHome} data-testid="error-go-home-btn">
              Ir al inicio
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
